import { useState, type JSX } from "react";
import { Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import { ChevronDownRegular, ChevronUpRegular, FilterFilled, MoreVerticalRegular } from "@fluentui/react-icons";
import { formatNumericCalendarDate } from "@/utils/calendar-date";
import { initialsFromName } from "@/utils/initials";
import type { MergersAndAcquisitionsRoleGroup, MergersAndAcquisitionsRow } from "./deal-tracker-types";
import {
  FRESHNESS_LABELS,
  TEAM_COLUMNS,
  hasActiveFilters,
  lastUpdateFreshness,
  visibleBankers,
  type LastUpdateFreshness,
  type NumberFilterOperator,
  type TeamColumn,
  type TeamFilter,
  type TeamFilters,
  type TeamSort,
} from "./team-grid";

const NUMBER_OPERATORS: { value: NumberFilterOperator; label: string }[] = [
  { value: "gte", label: "At least" },
  { value: "lte", label: "At most" },
  { value: "eq", label: "Exactly" },
];

interface ColumnMenuProps {
  column: TeamColumn;
  filter: TeamFilter | undefined;
  onSort: (sort: TeamSort | null) => void;
  onFilter: (filter: TeamFilter | undefined) => void;
}

/**
 * The per-column overflow menu (TAD-6072 AC 4). A popover rather than a Fluent
 * Menu because the filters need real inputs, which a menu's keyboard model
 * fights with.
 */
function ColumnMenu({ column, filter, onSort, onFilter }: ColumnMenuProps): JSX.Element {
  const isFiltered = filter !== undefined;

  // The comparison is draft UI state until a value is typed. Committing it on
  // its own would build a filter with value 0 -- picking "At most" would hide
  // every positive row while the value box still looked empty.
  const [draftOperator, setDraftOperator] = useState<NumberFilterOperator>("gte");
  const operator = filter?.kind === "number" ? filter.operator : draftOperator;

  return (
    <Popover positioning="below-end" withArrow>
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={
            isFiltered
              ? "deal-tracker-team__head-menu deal-tracker-team__head-menu--filtered"
              : "deal-tracker-team__head-menu"
          }
          aria-label={`${column.label} column options${isFiltered ? " (filtered)" : ""}`}
        >
          {isFiltered ? <FilterFilled /> : <MoreVerticalRegular />}
        </button>
      </PopoverTrigger>

      <PopoverSurface className="deal-tracker-team__menu">
        <div className="deal-tracker-team__menu-group">
          <button type="button" onClick={() => onSort({ key: column.key, direction: "asc" })}>
            Sort ascending
          </button>
          <button type="button" onClick={() => onSort({ key: column.key, direction: "desc" })}>
            Sort descending
          </button>
          <button type="button" onClick={() => onSort(null)}>
            Clear sort
          </button>
        </div>

        <div className="deal-tracker-team__menu-group deal-tracker-team__menu-group--filter">
          {column.filter === "text" ? (
            <label>
              <span>Name contains</span>
              <input
                type="text"
                value={filter?.kind === "text" ? filter.value : ""}
                onChange={(event) =>
                  onFilter(event.target.value ? { kind: "text", value: event.target.value } : undefined)
                }
              />
            </label>
          ) : null}

          {column.filter === "number" ? (
            <label>
              <span>Show rows where</span>
              <span className="deal-tracker-team__menu-number">
                <select
                  aria-label={`${column.label} comparison`}
                  value={operator}
                  onChange={(event) => {
                    const next = event.target.value as NumberFilterOperator;
                    setDraftOperator(next);
                    // Only re-filter if a value is already committed.
                    if (filter?.kind === "number") {
                      onFilter({ kind: "number", operator: next, value: filter.value });
                    }
                  }}
                >
                  {NUMBER_OPERATORS.map((operator) => (
                    <option key={operator.value} value={operator.value}>
                      {operator.label}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  aria-label={`${column.label} value`}
                  value={filter?.kind === "number" ? String(filter.value) : ""}
                  onChange={(event) =>
                    onFilter(
                      event.target.value === ""
                        ? undefined
                        : { kind: "number", operator, value: Number(event.target.value) }
                    )
                  }
                />
              </span>
            </label>
          ) : null}

          {column.filter === "freshness" ? (
            <label>
              <span>Freshness</span>
              <select
                value={filter?.kind === "freshness" ? filter.value : ""}
                onChange={(event) =>
                  onFilter(
                    event.target.value
                      ? { kind: "freshness", value: event.target.value as LastUpdateFreshness }
                      : undefined
                  )
                }
              >
                <option value="">Any</option>
                {(Object.keys(FRESHNESS_LABELS) as LastUpdateFreshness[]).map((band) => (
                  <option key={band} value={band}>
                    {FRESHNESS_LABELS[band]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <button type="button" onClick={() => onFilter(undefined)} disabled={!isFiltered}>
            Clear filter
          </button>
        </div>
      </PopoverSurface>
    </Popover>
  );
}

export interface DealTrackerTeamProps {
  roles: MergersAndAcquisitionsRoleGroup[];
  /** Types of the open roles; any number of accordions may be open at once. */
  expandedRoles: readonly string[];
  onToggleRole: (type: string) => void;
  onSelectBanker: (banker: MergersAndAcquisitionsRow) => void;
  /** Injected so the freshness banding is deterministic under test. */
  today?: Date;
}

/**
 * The Staffer's team as one grid (TAD-6072): shared column headers across both
 * role accordions, so every Banker's metrics line up whichever roles are open.
 * A role with no Bankers renders its bar but does not expand — there is no empty
 * state to show.
 */
export function DealTrackerTeam({
  roles,
  expandedRoles,
  onToggleRole,
  onSelectBanker,
  today = new Date(),
}: DealTrackerTeamProps): JSX.Element {
  const [sort, setSort] = useState<TeamSort | null>(null);
  const [filters, setFilters] = useState<TeamFilters>({});
  const filtering = hasActiveFilters(filters);

  return (
    <div className="deal-tracker-team">
      <table className="deal-tracker-team__table" aria-label="Banker Deal Trackers">
        <thead>
          <tr>
            {TEAM_COLUMNS.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={column.numeric ? "deal-tracker-team__numeric" : undefined}
                aria-sort={sort?.key === column.key ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
              >
                <span className="deal-tracker-team__head-cell">
                  <span>{column.label}</span>
                  <ColumnMenu
                    column={column}
                    filter={filters[column.key]}
                    onSort={setSort}
                    onFilter={(filter) => setFilters((current) => ({ ...current, [column.key]: filter }))}
                  />
                </span>
              </th>
            ))}
          </tr>
        </thead>

        {roles.map((role) => {
          const isEmpty = role.Bankers.length === 0;
          const isExpanded = !isEmpty && expandedRoles.includes(role.Type);
          const shown = isExpanded ? visibleBankers(role.Bankers, filters, sort, today) : [];

          return (
            <tbody key={role.Type} className="deal-tracker-team__group">
              <tr className="deal-tracker-team__bar-row">
                {/* A td, not a th: this bar groups rows, it does not head a column. */}
                <td colSpan={TEAM_COLUMNS.length}>
                  <button
                    type="button"
                    className="deal-tracker-team__bar"
                    aria-expanded={isExpanded}
                    disabled={isEmpty}
                    onClick={() => onToggleRole(role.Type)}
                  >
                    {isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
                    <span className="deal-tracker-team__bar-label">{role.Label}</span>
                    <span className="deal-tracker-team__count">
                      {/* While filtering, the bar counts what is actually listed --
                          a count that ignored the filter would just look wrong. */}
                      {isExpanded && filtering
                        ? `${shown.length} of ${role.Count} Bankers`
                        : `${role.Count} Banker${role.Count === 1 ? "" : "s"}`}
                    </span>
                  </button>
                </td>
              </tr>

              {isExpanded && shown.length === 0 ? (
                <tr>
                  <td colSpan={TEAM_COLUMNS.length} className="deal-tracker-team__no-match">
                    No Bankers match the current filters.
                  </td>
                </tr>
              ) : null}

              {shown.map((banker) => {
                const freshness = lastUpdateFreshness(banker.LastUpdate, today);
                return (
                  <tr
                    key={banker.UserId}
                    className="deal-tracker-team__banker-row"
                    // The whole row is a click target, but the row keeps its
                    // table semantics -- the name is the real control, so
                    // keyboard and screen-reader users get a button, not a
                    // <tr> pretending to be one.
                    onClick={() => onSelectBanker(banker)}
                  >
                    <th scope="row">
                      <span className="deal-tracker-team__banker">
                        <span className="deal-tracker-team__avatar" aria-hidden="true">
                          {initialsFromName(banker.Name)}
                        </span>
                        <button
                          type="button"
                          className="deal-tracker-team__banker-name"
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelectBanker(banker);
                          }}
                        >
                          {banker.Name}
                        </button>
                      </span>
                    </th>
                    <td className="deal-tracker-team__numeric">{banker.ActiveSellSideDeals ?? 0}</td>
                    <td className="deal-tracker-team__numeric">{banker.ActiveBuySideDeals ?? 0}</td>
                    <td className="deal-tracker-team__numeric">{banker.ActiveBakeOffsPitches ?? 0}</td>
                    <td className="deal-tracker-team__numeric">{banker.WeekendHours ?? 0}</td>
                    <td className={`deal-tracker-team__last-update deal-tracker-team__last-update--${freshness}`}>
                      {freshness === "never" ? "Never" : formatNumericCalendarDate(banker.LastUpdate ?? undefined)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}
